class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.thumbnailSlider = null;
    this.maxPageWidth = parseFloat(getComputedStyle(this.closest('.shopify-section')).getPropertyValue('max-width'));
    this.resizeObserver = null;
    this.thumbnailResizeObserver = null;
    this.thumbnailsLayoutDesktop = this.getAttribute('data-thumbnails-layout-desktop') || 'bottom';
    this.handleWindowResize = null;
  }

  connectedCallback() {
    this.slider = this.initMainSlider();
    this.slider.on('mounted updated refresh resized', () => this.updateEmptyState());

    if (this.querySelector('[data-ref="thumbnail-slider"]')) {
      this.thumbnailSlider = this.initThumbnailSlider();
    }

    if (this.thumbnailSlider) {
      this.slider.sync(this.thumbnailSlider);
    }

    this.slider.mount();

    if (this.thumbnailSlider) {
      this.thumbnailSlider.mount();
      if (this.thumbnailsLayoutDesktop === 'left') {
        this.updateThumbnailSliderHeight();
        this.thumbnailResizeObserver = new ResizeObserver(() => {
          this.updateThumbnailSliderHeight();
        });

        const mainSliderElement = this.querySelector('[data-ref="main-slider"]');
        if (mainSliderElement) {
          this.thumbnailResizeObserver.observe(mainSliderElement);
        }

        this.handleWindowResize = () => this.updateThumbnailSliderHeight();
        window.addEventListener('resize', this.handleWindowResize);
      }
    }

    if (!this.thumbnailSlider) {
      this.slider.on('mounted', () => {
        this.updateSize();
      });

      // Utiliser ResizeObserver pour détecter tous les changements de taille
      this.resizeObserver = new ResizeObserver(() => {
        this.updateSize();
      });
      this.resizeObserver.observe(document.documentElement);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.thumbnailResizeObserver) {
      this.thumbnailResizeObserver.disconnect();
    }
    if (this.handleWindowResize) {
      window.removeEventListener('resize', this.handleWindowResize);
    }
  }

  initMainSlider() {
    const perPageDesktop = this.getAttribute('data-per-page-desktop');
    let perPageMobile = this.getAttribute('data-per-page-mobile');
    const gap = this.getAttribute('data-gap') + 'px';
    const showArrows = this.getAttribute('data-show-arrows') === 'true';
    const showArrowsMobile = this.getAttribute('data-show-arrows-mobile') === 'true';
    const showPagination = this.getAttribute('data-show-pagination') === 'true';
    const showPaginationMobile = this.getAttribute('data-show-pagination-mobile') === 'true';
    const autoplay = this.getAttribute('data-autoplay') === 'true';
    const autoplaySpeed = this.getAttribute('data-autoplay-speed') + '000';
    const rewind = this.getAttribute('data-rewind') === 'true';

    if (perPageMobile === '1.8') perPageMobile = 1;

    return new Splide(this.querySelector('[data-ref="main-slider"]'), {
      perPage: perPageMobile,
      perMove: 1,
      rewind: rewind,
      gap: gap,
      drag: true,
      snap: true,
      arrows: showArrows,
      arrowsMobile: showArrowsMobile,
      pagination: showPaginationMobile,
      autoplay: autoplay,
      interval: autoplaySpeed,
      autoHeight: true,
      lazyLoad: false,
      flickMaxPages: 1,
      flickPower: 400,
      mediaQuery: 'min',
      breakpoints: {
        750: {
          perPage: perPageDesktop,
          pagination: showPagination,
          arrows: showArrows,
        },
      },
    });
  }

  initThumbnailSlider() {
    const showArrows = this.getAttribute('data-show-arrows-thumbnails') === 'true';
    const desktopOptions =
      this.thumbnailsLayoutDesktop === 'left'
        ? {
            direction: 'ttb',
            height: '100%',
            fixedWidth: 90,
            fixedHeight: 90,
          }
        : {
            fixedWidth: 90,
            fixedHeight: 90,
          };

    return new Splide(this.querySelector('[data-ref="thumbnail-slider"]'), {
      fixedWidth: 70,
      fixedHeight: 70,
      gap: 10,
      rewind: false,
      pagination: false,
      isNavigation: true,
      arrows: showArrows,
      mediaQuery: 'min',
      breakpoints: {
        750: {
          ...desktopOptions,
        },
      },
    });
  }

  updateThumbnailSliderHeight() {
    if (this.thumbnailsLayoutDesktop !== 'left') return;

    const mainSliderElement = this.querySelector('[data-ref="main-slider"]');
    const thumbnailSliderElement = this.querySelector('[data-ref="thumbnail-slider"]');

    if (!(mainSliderElement instanceof HTMLElement) || !(thumbnailSliderElement instanceof HTMLElement)) return;

    if (window.innerWidth <= 750) {
      thumbnailSliderElement.style.height = '';
      return;
    }

    const mainTrack = mainSliderElement.querySelector('.splide__track');
    const heightSource = mainTrack instanceof HTMLElement ? mainTrack.offsetHeight : mainSliderElement.offsetHeight;

    if (!heightSource) {
      thumbnailSliderElement.style.height = '';
      return;
    }

    thumbnailSliderElement.style.height = `${heightSource}px`;
  }

  updateSize() {
    const pageWidthContainer = this.closest('.page-width');
    const referenceContainer = pageWidthContainer || this.closest('.shopify-section');

    if (!(referenceContainer instanceof HTMLElement)) return;

    const referenceRect = referenceContainer.getBoundingClientRect();
    const padding = Math.max(referenceRect.left, 0);

    this.style.width = `calc(100% + ${2 * padding}px)`;
    this.style.marginLeft = `-${padding}px`;
    this.style.marginRight = `-${padding}px`;

    if (this.querySelector('.splide__track')) {
      this.querySelector('.splide__track').style.paddingRight = `${padding}px`;
      this.querySelector('.splide__track').style.paddingLeft = `${padding}px`;
    }

    if (this.querySelector('.splide__arrow--prev') && this.querySelector('.splide__arrow--next')) {
      this.querySelector('.splide__arrow--prev').style.left = `${padding + 10}px`;
      this.querySelector('.splide__arrow--next').style.right = `${padding + 10}px`;
    }
  }

  remove(index) {
    this.slider.remove(index);
    requestAnimationFrame(() => this.updateEmptyState());
  }

  updateEmptyState() {
    const sliderBlock = this.closest('.slider-block');
    if (!sliderBlock) return;

    const slides = this.querySelectorAll('.splide__list > .splide__slide');
    sliderBlock.classList.toggle('slider-block--empty', slides.length === 0);
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}
